import { checkLanguageConfiguration } from "@/config/language-rules";
import { getDerivedLanguages } from "./languageSetup";

type FormValues = Record<string, any>;

// The llm_configuration, stt_configuration and tts_configuration objects that a save writes to
// the assistants row. Shared with the latency probe, which sends the unsaved form in the same shape.
export function buildProviderConfigs(formData: FormValues) {
  const {
    llm_provider,
    temperature,
    maxTokens,
    detectEmotion,
    contextWindow,
    responseFormat,
    enableStreaming,
    serviceTier,

    stt_provider,
    stt_model: deepgramModel,
    language,
    enableFiltering,
    useNumerals,
    backgroundDenoising,
    keyterms,
    endpointing_ms,
    enable_language_detection,
    transcriber_ws_url,
    stt_sample_rate,
    stt_mode,

    voice,
    tts_model,
    tts_language,
    speed,
    pitch,
    stability,
    similarity_boost,
    style,
    clarity,
    styleExaggeration,
    backgroundSound,
    backgroundSoundUrl,
    inputMinCharacters,
    punctuationBoundaries,
    optimizeStreaming,
    useSpeakerBoost,
  } = formData;

  const llmConfig: Record<string, unknown> = {
    temperature,
    maxTokens,
    detectEmotion,
    contextWindow,
    responseFormat,
    enableStreaming,
  };

  // Persist base URL and API key for custom/livekit-openai/openai-realtime providers
  if (
    llm_provider === "custom" ||
    llm_provider === "livekit-openai" ||
    llm_provider === "openai-realtime" ||
    llm_provider === "custom-realtime"
  ) {
    const { baseUrl, apiKey } = formData as { baseUrl?: string; apiKey?: string };
    if (baseUrl) llmConfig.baseUrl = baseUrl;
    if (apiKey) llmConfig.apiKey = apiKey;
  }

  // Persist voice/language for realtime speech-to-speech providers (Gemini Live)
  if (llm_provider === "google-realtime") {
    const { realtimeVoice, realtimeLanguage } = formData as {
      realtimeVoice?: string;
      realtimeLanguage?: string;
    };
    llmConfig.realtimeVoice = realtimeVoice || "Puck";
    llmConfig.realtimeLanguage = realtimeLanguage || "en-US";
  }

  // service_tier is an OpenAI-only request parameter. Always written for
  // openai so the backend never has to guess a default.
  if (llm_provider === "openai") {
    llmConfig.serviceTier = serviceTier || "priority";
  }

  // With a language setting the providers' language fields follow it. No setting, or a
  // realtime provider: derived is empty and the form's own fields are saved as before.
  const derivedLanguages = getDerivedLanguages(formData);

  const sttConfig: Record<string, unknown> = {
    deepgramModel,
    language: derivedLanguages.stt ?? language,
    enableFiltering,
    useNumerals,
    backgroundDenoising,
    keyterms: keyterms ? keyterms.split(' ').filter((term: string) => term.trim() !== '') : [],
    endpointing_ms,
    enable_language_detection,
  };

  if (stt_provider === "whisper_ws" && transcriber_ws_url) {
    sttConfig.wsUrl = transcriber_ws_url;
  }

  // Only Gnani and Smallest AI read a sample rate off the STT config.
  if (["gnani", "smallestai"].includes(stt_provider) && stt_sample_rate) {
    sttConfig.sampleRate = stt_sample_rate;
  }

  // Only Sarvam realtime reads mode off the STT config (see
  // AgentService.buildCallConfig's sarvam-realtime crossover, v2 server).
  if (stt_provider === "sarvam-realtime") {
    sttConfig.mode = stt_mode || "codemix";
  }

  const voiceId = voice ? String(voice).trim() : "21m00Tcm4TlvDq8ikWAM";

  // Convert Cartesia string speed values to numeric values for backend compatibility
  let numericSpeed = speed;
  if (formData.tts_provider === 'cartesia' && typeof speed === 'string') {
    switch (speed) {
      case 'slow':
        numericSpeed = 0.8;
        break;
      case 'fast':
        numericSpeed = 1.2;
        break;
      case 'normal':
      default:
        numericSpeed = 1.0;
        break;
    }
  }

  const ttsConfig: Record<string, unknown> = {
    voice: voiceId,
    tts_model,
    speed: numericSpeed,
    pitch,
    stability,
    similarity_boost,
    style,
    clarity,
    styleExaggeration,
    backgroundSound,
    backgroundSoundUrl,
    inputMinCharacters,
    punctuationBoundaries,
    optimizeStreaming,
    useSpeakerBoost,
  };

  // Cartesia-only: Murf's own Language field still writes the shared STT
  // `language` key (untouched here), so setting this unconditionally would
  // leak a stale Cartesia selection into tts_configuration for Murf agents.
  if (formData.tts_provider === "cartesia" && tts_language) {
    ttsConfig.language = tts_language;
  }
  if (derivedLanguages.tts) {
    ttsConfig.language = derivedLanguages.tts;
  }

  return { llmConfig, sttConfig, ttsConfig };
}

// The assistants columns the latency probe overrides with the current, possibly unsaved, form.
export function buildProbeOverrides(formData: FormValues) {
  const { llmConfig, sttConfig, ttsConfig } = buildProviderConfigs(formData);
  return {
    system_prompt: formData.systemPrompt,
    llm_provider: formData.llm_provider,
    llm_model: formData.llm_model,
    llm_configuration: llmConfig,
    stt_provider: formData.stt_provider,
    stt_model: formData.stt_model,
    stt_configuration: sttConfig,
    tts_provider: formData.tts_provider,
    tts_model: formData.tts_model,
    tts_configuration: ttsConfig,
    language_configuration: checkLanguageConfiguration(formData.language_configuration),
  };
}
